import { useLocation } from "@solidjs/router"
import { createContext, createEffect, on, onCleanup, type ParentProps, useContext } from "solid-js"
import { sessionValues } from "@/storage/browser"
import { getDeployedBuildID } from "@/transport/app-build"
import { reloadWithCacheBust } from "@/utils/reload"
import { showAttentionToast, toaster } from "@mux/ui/toast"

const CURRENT_BUILD_ID = typeof __MUX_BUILD_ID__ === "string" ? __MUX_BUILD_ID__ : ""

const reloadTargetKey = "mux.app-update-reload-target"
const CHECK_INTERVAL_MS = 60_000
// A resume after this long away reloads into a newer build instead of
// offering it: the user is starting over rather than continuing a task.
const LONG_ABSENCE_MS = 5 * 60_000
const CHECK_TIMEOUT_MS = 5_000
// Longest a reload waits for in-flight saves before giving up and staying on
// this build; local storage writes finish in milliseconds.
const HOLD_TIMEOUT_MS = 2_000

type ListenerTarget = Pick<EventTarget, "addEventListener" | "removeEventListener">

type AppUpdateOptions = {
  currentBuild?: string
  fetchBuild?: (signal: AbortSignal) => Promise<string>
  reload?: () => void
  /** Tells the user a newer build is ready; `apply` reloads into it. Returns a function that withdraws the offer. */
  offer?: (apply: () => void) => () => void
  storage?: Pick<Storage, "getItem" | "setItem">
  visibility?: ListenerTarget & Pick<Document, "visibilityState">
  page?: ListenerTarget
  now?: () => number
  setTimer?: (callback: () => void, delay: number) => number
  clearTimer?: (timer: number) => void
}

function offerReload(apply: () => void) {
  const id = showAttentionToast({
    title: "A new version of Kettle is ready",
    actions: [{ label: "Reload", onClick: apply }],
  })
  return () => void toaster.dismiss(id)
}

/**
 * Keeps a long-lived client (notably an installed web app, which resumes
 * without reloading) on the frontend build this origin serves. Deploys keep
 * the old build's chunks, so a stale client never fails to load code; it would
 * keep running against servers whose protocol has moved on. Once a check finds
 * a newer build the client is stale, and it moves to that build at the first
 * moment a reload costs nothing:
 *
 * - the next route change, which already replaces the page;
 * - a resume after a long absence;
 * - an explicit refresh (`check`), or the offer's Reload action.
 *
 * A short resume only offers the build: a reload there would replace the page
 * the user came back to mid-task.
 */
export function createAppUpdateController(options: AppUpdateOptions = {}) {
  const currentBuild = options.currentBuild ?? CURRENT_BUILD_ID
  const fetchBuild = options.fetchBuild ?? getDeployedBuildID
  const reload = options.reload ?? reloadWithCacheBust
  const offer = options.offer ?? offerReload
  const storage = options.storage ?? sessionValues
  const visibility = options.visibility ?? (typeof document === "undefined" ? undefined : document)
  const page = options.page ?? (typeof window === "undefined" ? undefined : window)
  const now = options.now ?? Date.now
  const setTimer = options.setTimer ?? ((callback, delay) => window.setTimeout(callback, delay))
  const clearTimer = options.clearTimer ?? ((handle) => window.clearTimeout(handle))

  let lastCheck = now()
  let pending: Promise<string | undefined> | undefined
  let inflight: AbortController | undefined
  let newer: string | undefined
  let withdrawOffer: (() => void) | undefined
  let hiddenAt: number | undefined
  let reloading = false
  let disposed = false
  const holds = new Set<() => Promise<unknown>>()

  const readReloadTarget = () => {
    try {
      return storage.getItem(reloadTargetKey)
    } catch {
      return null
    }
  }

  /** The deployed build, when it is newer than this page and no reload has already targeted it. */
  const fetchNewerBuild = async () => {
    const controller = new AbortController()
    inflight = controller
    const timer = setTimer(() => controller.abort(), CHECK_TIMEOUT_MS)
    try {
      const deployed = await fetchBuild(controller.signal)
      if (!deployed || deployed === currentBuild) return undefined
      // A reload already targeted this build and still produced the old bundle
      // (for example a stale edge copy of index.html); do not reload again.
      if (readReloadTarget() === deployed) return undefined
      return deployed
    } catch {
      // The check is advisory: offline, timeouts, and hosts without build
      // metadata keep the running version rather than failing the caller.
      return undefined
    } finally {
      clearTimer(timer)
      if (inflight === controller) inflight = undefined
    }
  }

  const newerBuild = () => {
    lastCheck = now()
    pending ??= fetchNewerBuild().finally(() => {
      pending = undefined
    })
    return pending
  }

  const apply = (build: string) => {
    if (reloading) return
    reloading = true
    const go = () => {
      try {
        storage.setItem(reloadTargetKey, build)
      } catch {}
      reload()
    }
    if (!holds.size) return go()
    // A reload aborts unfinished writes, so let the ones in flight finish.
    // When they do not, stay on this build: the next chance tries again.
    let timer: number | undefined
    const finished = Promise.allSettled([...holds].map((wait) => Promise.resolve().then(wait))).then(() => true)
    const timedOut = new Promise<boolean>((resolve) => {
      timer = setTimer(() => resolve(false), HOLD_TIMEOUT_MS)
    })
    void Promise.race([finished, timedOut]).then((saved) => {
      if (timer !== undefined) clearTimer(timer)
      if (disposed) return
      if (saved) go()
      else reloading = false
    })
  }

  /** Resolves true when a newer build was found and the page is reloading. */
  const check = async (): Promise<boolean> => {
    if (reloading) return true
    if (!currentBuild || disposed) return false
    const build = await newerBuild()
    if (build && !disposed) apply(build)
    return reloading
  }

  /** Marks this client stale and offers the build, once per build. */
  const found = (build: string) => {
    if (build === newer) return
    newer = build
    withdrawOffer?.()
    withdrawOffer = offer(() => apply(build))
  }

  /** Checks in the background when the last check is old enough. */
  const recheck = (onNewer: (build: string) => void) => {
    if (!currentBuild || disposed || reloading) return
    if (now() - lastCheck < CHECK_INTERVAL_MS) return
    void newerBuild().then((build) => {
      if (build && !disposed && !reloading) onNewer(build)
    })
  }

  /** Called after each route change: a stale client loads the new route on the deployed build. */
  const navigated = () => {
    if (newer) apply(newer)
    else recheck(found)
  }

  const onVisibilityChange = () => {
    if (visibility?.visibilityState !== "visible") {
      hiddenAt ??= now()
      return
    }
    const away = hiddenAt === undefined ? 0 : now() - hiddenAt
    hiddenAt = undefined
    const reloadInto = (build: string) => (away >= LONG_ABSENCE_MS ? apply(build) : found(build))
    // A known newer build is reused only while fresh; a later deploy may have replaced it.
    if (newer && now() - lastCheck < CHECK_INTERVAL_MS) reloadInto(newer)
    else recheck(reloadInto)
  }
  const onPageShow = (event: Event) => {
    if ((event as PageTransitionEvent).persisted) onVisibilityChange()
  }
  visibility?.addEventListener("visibilitychange", onVisibilityChange)
  page?.addEventListener("pageshow", onPageShow)

  return {
    check,
    navigated,
    /** Makes reloads wait for `wait` (unfinished saves); returns a function that removes it. */
    hold(wait: () => Promise<unknown>) {
      holds.add(wait)
      return () => void holds.delete(wait)
    },
    dispose() {
      disposed = true
      visibility?.removeEventListener("visibilitychange", onVisibilityChange)
      page?.removeEventListener("pageshow", onPageShow)
      inflight?.abort()
      withdrawOffer?.()
    },
  }
}

type AppUpdate = Pick<ReturnType<typeof createAppUpdateController>, "check" | "navigated" | "hold">
const AppUpdateContext = createContext<AppUpdate>()

export function AppUpdateController(props: ParentProps) {
  const controller = createAppUpdateController()
  onCleanup(controller.dispose)
  return <AppUpdateContext.Provider value={controller}>{props.children}</AppUpdateContext.Provider>
}

function useAppUpdate() {
  const update = useContext(AppUpdateContext)
  if (!update) throw new Error("useAppUpdate must be used inside AppUpdateController")
  return update
}

export function useAppUpdateCheck() {
  return useAppUpdate().check
}

/** Makes update reloads wait for this owner's unfinished saves while it is mounted. */
export function useAppUpdateHold(wait: () => Promise<unknown>) {
  onCleanup(useAppUpdate().hold(wait))
}

/**
 * Reports route changes to the update controller. Mounted inside the router;
 * it acts after the router has committed a route, so leave guards (unsaved
 * edits) have already had their say.
 */
export function AppUpdateNavigation() {
  const update = useAppUpdate()
  const location = useLocation()
  createEffect(on(() => location.pathname, () => update.navigated(), { defer: true }))
  return null
}
